'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionIdentity, SessionCheckResult } from './session';
import { fetchSession as fetchSessionApi } from './client';
import { getStoredUserId, getUserData, saveLocalStorageData } from '@/helpers/local-storage';
import { clearInvalidSession } from '@/utils/auth/common/clear-session';
import { buildAbsoluteRedirectUrl, normalizeUserFields } from '@/utils/auth/common/auth-storage';
import logout from '@/utils/auth/common/logout';
import getUser from '@/utils/get-user';

/** Single React Query key for auth state — no second cache, so no two answers to disagree. */
export const AUTH_USER_KEY = ['auth-user'] as const;

/** Client-side user shape (superset of server SessionUser) — profile fields (name/avatar/tags) live in localStorage/GET /v1/users, not the minimal server session. */
export type AuthUser = {
  uid?: string;
  email?: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  profilePicture?: string;
  tags?: string[];
  [key: string]: unknown;
};

/** Prior name for this shape, kept so existing `safeParse<UserData>` call sites read unchanged. */
export type UserData = AuthUser;

/** localStorage profile, but only if it actually identifies someone (email present). */
function localUser(): AuthUser | null {
  const u = getUserData();
  return u && typeof u === 'object' && u.email ? (u as AuthUser) : null;
}

/**
 * True when we know who the user is but hold none of the display fields the UI renders
 * (identity came from the server probe or a bare stored blob). `tags` is deliberately
 * excluded: the probe returns it on every valid verdict, so including it let a
 * profile-less user stay a nameless shell forever (enrichWithProfile never re-ran). A
 * real profile always has one field (registration requires a name), so this only fires
 * once — worst case one extra /v1/users call, not a loop.
 */
function needsProfile(user: AuthUser): boolean {
  return !user.firstName && !user.lastName && !user.company && !user.profilePicture;
}

/**
 * Fills in profile fields from GET /v1/users and persists them. Uses saveLocalStorageData,
 * not setAuthUserInStorage — the latter fires userDataUpdated, which SessionHydrator would
 * turn into a refetch of this same query. getUser() can't reject (axiosHandler returns
 * errors, not rejections), so the catch here only guards JSON.stringify; a missing
 * profile is never a reason to report the user as logged out.
 */
async function enrichWithProfile(identity: AuthUser): Promise<AuthUser> {
  try {
    const response = await getUser();
    if (response?.status !== 200 || !('data' in response) || !response.data) return identity;
    // normalizeUserFields matches every other writer of this key: some Academy API
    // bodies are snake_case, the UI reads camelCase.
    const merged = normalizeUserFields({ ...identity, ...(response.data as AuthUser) });
    saveLocalStorageData('user', JSON.stringify(merged));
    return merged;
  } catch {
    return identity;
  }
}

/**
 * Resolves auth state from the server verdict, decorated with the stored profile. Named
 * (not an inline closure) so queryClient.fetchQuery() can also drive it directly.
 */
async function fetchAuthUser(): Promise<AuthUser | null> {
  const local = localUser();

  // A rejection (offline, blip) isn't a verdict — same non-answer as 'uncertain' below —
  // so fall back to localStorage rather than erroring and reading a signed-in user as logged out.
  let session: Awaited<ReturnType<typeof fetchSessionApi>>;
  try {
    session = await fetchSessionApi();
  } catch {
    return local;
  }

  const { ok, body } = session;
  const status = (body as { sessionStatus?: SessionCheckResult['status'] } | null)?.sessionStatus;

  // No confirmed answer ('uncertain' = API unreachable) — keep the last-known user
  // rather than logging someone out over a blip.
  if (!ok || !status || status === 'uncertain') return local;

  if (status === 'valid') {
    // Prefer the richer local profile; the probe carries no email/display fields, so
    // those only come from cache or enrich (which runs only when genuinely absent).
    const server = body as SessionIdentity;

    // ...but only when the two describe the SAME person. Switching identity server-side
    // (e.g. "Log In As Partner") replaces connect.sid without touching localStorage, so
    // a stale cached identity would keep old tags and mis-gate partner content. Only a
    // proven id mismatch discards the cache — a stored blob with no id is just old, not wrong.
    const localId = getStoredUserId(local);
    const conflicts = Boolean(localId && server.uid && localId !== server.uid);

    const identity: AuthUser =
      local && !conflicts
        ? local
        : { uid: server.uid, role: server.role ?? undefined, tags: server.tags };

    // Force a profile refetch on conflict even though the probe's tags would satisfy
    // needsProfile() — the cached name/avatar belong to the wrong person.
    return conflicts || needsProfile(identity) ? enrichWithProfile(identity) : identity;
  }

  // 'invalid' | 'no-cookie': server says nobody, and is in a position to know. Client-only
  // clear, never a server logout — a server-side clearCookie would be unrecoverable if the
  // verdict were only a transient signature failure. Guarded because clearInvalidSession()
  // fires userLoggedOut, which shouldn't happen on every anonymous page load.
  if (local) clearInvalidSession();
  return null;
}

/**
 * Single query definition so anything resolving auth outside a component
 * (queryClient.fetchQuery) shares the same cache entry and in-flight request as every
 * useAuthUser() on the page, instead of duplicating the /api/auth/session + /v1/users pair.
 */
export const authUserQueryOptions = {
  queryKey: AUTH_USER_KEY,
  queryFn: fetchAuthUser,
  staleTime: 60 * 1000,
  retry: false,
} as const;

/**
 * Canonical client-side auth hook — use instead of reading localStorage/isUserLoggedIn()
 * directly. The server verdict decides logged-in/out; localStorage only decorates the
 * display, so auth-state drift is structurally impossible here rather than patched.
 * Reactive to auth events (subscribed once in <SessionHydrator/>, not here) so it updates
 * live — this matters because PartnerSessionSync syncs localStorage in place without a
 * reload, so a one-time isUserLoggedIn() snapshot can miss the login completing.
 */
export function useAuthUser() {
  const query = useQuery(authUserQueryOptions);

  // Auth-event listeners live in <SessionHydrator/> (mounts once), so one event triggers
  // one /api/auth/session request, not one per hook consumer.

  const user = query.data ?? null;
  return {
    user,
    // A resolved user IS the verdict (queryFn returns null for every logged-out case) —
    // not Boolean(user?.email): the probe carries no email, which would read a fresh
    // login as logged out.
    isLoggedIn: Boolean(user),
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/**
 * The one logout path. Ends the server session, then hard-navigates home — a full reload
 * rather than router.replace()+refresh(), since relying on cache/event timing across every
 * consumer (SessionHydrator, staleTime, nav components) was a real source of bugs. A
 * reload re-derives everything from scratch instead.
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // End the server session first (see utils/auth/common/logout.ts for ordering),
      // then clear the query cache as a backstop before the reload.
      await logout();
      queryClient.setQueryData(AUTH_USER_KEY, null);
    },
    onSettled: () => {
      window.location.href = buildAbsoluteRedirectUrl('/');
    },
  });
}
